import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Copy, FileText, Package } from 'lucide-react';
import { api } from '@/lib/api';
import { moeda, numero } from '@/lib/utils';
import { ImagemProduto } from '@/componentes/produto/ImagemProduto';
import { LinhasEsqueleto } from '@/componentes/ui/primitivos';
import { BaixarXml, Dado, Secao, SituacaoNf, quando } from './RaioXPedido';

/**
 * RAIO-X DA NOTA SEM PEDIDO — transferência entre filiais, nota manual.
 *
 * É o que a busca acha pelo número da NF quando não há televenda atrás. Sem
 * pedido não há status, separação nem romaneio: a pergunta é para quem foi,
 * quem emitiu, se autorizou e o que foi dentro.
 */

interface RaioX {
  id: number;
  empresa: number;
  numero: number;
  serie: string | null;
  emissao: string | null;
  destinatario: string | null;
  usuario: string | null;
  total: number | null;
  chave: string | null;
  situacao: string | null;
  canceladaEm: string | null;
  temXml: boolean;
  temXmlCancelamento: boolean;
  /** `null` quando o banco não libera os itens (falta GRANT). */
  produtos:
    | {
        produto: number;
        descricao: string | null;
        ean: string | null;
        quantidade: number;
        valorUnitario: number | null;
        total: number | null;
      }[]
    | null;
}

export function RaioXNota({ id, aoVoltar }: { id: number; aoVoltar: () => void }): JSX.Element {
  const consulta = useQuery({
    queryKey: ['nota', id, 'raio-x'],
    queryFn: () => api.get<RaioX>(`/api/notas/${id}/raio-x`),
    staleTime: 30_000,
  });
  const r = consulta.data;
  /* TOTAL_LIQUIDO vem zerado em transferência: a soma dos itens é o valor real. */
  const total = r?.total
    ? r.total
    : r?.produtos
      ? r.produtos.reduce((a, p) => a + (p.total ?? 0), 0)
      : null;
  const unidades = r?.produtos?.reduce((a, p) => a + p.quantidade, 0) ?? 0;

  return (
    <div className="p-1.5">
      <button
        type="button"
        onClick={aoVoltar}
        className="mb-2 flex items-center gap-1.5 rounded-lg px-2 py-1 text-[12px] font-semibold text-primaria transition-colors hover:bg-primaria-suave"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Voltar aos resultados
      </button>

      {consulta.isLoading ? (
        <div className="p-4">
          <LinhasEsqueleto linhas={4} />
        </div>
      ) : consulta.isError || !r ? (
        <p className="mt-3 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[12.5px] text-status-divergencia">
          Não foi possível carregar a nota.
        </p>
      ) : (
        <>
          <div className="rounded-xl border border-borda bg-superficie2 p-3.5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  {/* Número de nota é identificador: sem separador de milhar. */}
                  <span className="tabular text-[20px] font-bold tracking-[-0.02em]">
                    NF {r.numero}
                    {r.serie ? ` · série ${r.serie}` : ''}
                  </span>
                  <span className="text-[12px] text-textoFraco">empresa {r.empresa}</span>
                  <span className="tabular text-[12px] text-textoFraco">{quando(r.emissao)}</span>
                </p>
                <p className="mt-0.5 truncate text-[13px] text-textoFraco">
                  {r.destinatario ?? 'destinatário não informado'}
                </p>
              </div>
              <div className="text-right">
                <span className="inline-block rounded-lg bg-superficie3 px-2.5 py-1 text-[13px] font-bold text-textoFraco">
                  sem pedido
                </span>
              </div>
            </div>
            <dl className="mt-3 grid gap-2 text-[13px] sm:grid-cols-4">
              <Dado rotulo="Situação">
                <SituacaoNf situacao={r.situacao} />
                {r.canceladaEm ? (
                  <span className="block text-[11px] text-textoFraco">cancelada {quando(r.canceladaEm)}</span>
                ) : null}
              </Dado>
              <Dado rotulo="Emitida por">{r.usuario ?? '—'}</Dado>
              <Dado rotulo="Total">
                {total != null ? moeda(total) : '—'}
                {r.produtos ? (
                  <span className="text-[11px] text-textoFraco"> · {numero(unidades)} un</span>
                ) : null}
              </Dado>
              <Dado rotulo="Arquivos">
                {r.temXml || r.temXmlCancelamento ? (
                  <span className="flex flex-wrap gap-1.5">
                    {r.temXml ? (
                      <>
                        <BaixarXml href={`/api/pedidos/notas/${r.id}/pdf`} rotulo="DANFE" pdf />
                        <BaixarXml href={`/api/pedidos/notas/${r.id}/xml`} rotulo="XML" />
                      </>
                    ) : null}
                    {r.temXmlCancelamento ? (
                      <BaixarXml href={`/api/pedidos/notas/${r.id}/xml?tipo=cancelamento`} rotulo="XML cancelamento" />
                    ) : null}
                  </span>
                ) : (
                  '—'
                )}
              </Dado>
            </dl>
            {r.chave ? (
              <button
                type="button"
                title="Copiar a chave de acesso"
                onClick={() => void navigator.clipboard?.writeText(r.chave ?? '')}
                className="tabular mt-2 inline-flex max-w-full items-center gap-1 truncate font-mono text-[11.5px] text-textoFraco hover:text-primaria"
              >
                <FileText className="h-3 w-3 shrink-0" />
                {r.chave}
                <Copy className="h-3 w-3 shrink-0 opacity-60" />
              </button>
            ) : null}
          </div>

          <Secao
            titulo={r.produtos ? `Produtos (${r.produtos.length})` : 'Produtos'}
            icone={<Package className="h-3.5 w-3.5" />}
            className="mt-2.5"
          >
            {r.produtos === null ? (
              <p className="text-[12.5px] text-textoFraco">
                Os itens da nota não estão liberados para o WMS no banco (falta o GRANT em
                NF_FATURAMENTO_PRODUTOS).
              </p>
            ) : r.produtos.length === 0 ? (
              <p className="text-[12.5px] text-textoFraco">Nota sem itens.</p>
            ) : (
              <ul className="divide-y divide-borda">
                {r.produtos.map((p, i) => (
                  <li key={`${p.produto}-${i}`} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
                    <ImagemProduto sku={p.produto} tamanho={44} alt={p.descricao ?? ''} />
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-2 text-[13px] font-medium leading-snug">{p.descricao}</p>
                      <p className="tabular text-[11px] text-textoFraco">
                        SKU {p.produto}
                        {p.ean ? ` · EAN ${p.ean}` : ''}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="tabular text-[14px] font-bold">{numero(p.quantidade)} un</p>
                      {p.total != null ? (
                        <p className="tabular text-[11px] text-textoFraco">{moeda(p.total)}</p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Secao>
        </>
      )}
    </div>
  );
}
